import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// PrimeNG imports
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputNumberModule } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';

// PrimeNG services
import { MessageService } from 'primeng/api';

// Services and models
import { UploadDetailConfigService } from './services/upload-detail-config.service';
import { UploadDetailConfig } from './models/upload-config.model';
import {
  FILE_CATEGORIES,
  SIZE_UNITS,
  IMAGE_FILE_FORMATS,
  FILE_FORMATS,
} from './constants/upload-config.constants';

// Shared components
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';

@Component({
  selector: 'app-upload-detail-config',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    SelectModule,
    MultiSelectModule,
    InputNumberModule,
    ButtonModule,
    ToastModule,
    TagModule,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './upload-detail-config.component.html',
  styleUrls: ['./upload-detail-config.component.css'],
})
export class UploadDetailConfigComponent implements OnInit {
  // Signals for reactive state management
  selectedSection = signal<string>('detail-images');
  loading = signal<boolean>(false);
  isEditMode = signal<boolean>(true);

  // Configuration data for each section
  detailImagesConfig: UploadDetailConfig = {
    page: 'product-asset',
    field: 'detail-images',
    fileCategory: 'IMAGE',
    fileFormat: ['jpg', 'jpeg', 'png', 'webp', 'bmp', 'svg'],
    fileSizeLimit: 2,
    sizeUnit: 'MB',
    maxDetailImages: 36,
  };

  mainImageConfig: UploadDetailConfig = {
    page: 'product-asset',
    field: 'main-image',
    fileCategory: 'IMAGE',
    fileFormat: ['jpg', 'jpeg', 'png'],
    fileSizeLimit: 2,
    sizeUnit: 'MB',
    maxDetailImages: 1, // Always 1 for main image
  };

  // Translation values for display
  get fileCategoryDisplay(): string {
    return this.translateService.instant(
      'upload_detail_config.file_categories.IMAGE'
    );
  }

  get sizeUnitDisplay(): string {
    return this.translateService.instant('upload_detail_config.size_units.MB');
  }

  /**
   * Get file format options based on file category
   */
  getFileFormatOptions(fileCategory: string): any[] {
    switch (fileCategory) {
      case 'IMAGE':
        return this.imageFileFormats;
      case 'DOCUMENT':
        return this.allFileFormats.filter((format) =>
          ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'].includes(
            format.value
          )
        );
      case 'VIDEO':
        return this.allFileFormats.filter((format) =>
          ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(format.value)
        );
      case 'AUDIO':
        return this.allFileFormats.filter((format) =>
          ['mp3', 'wav', 'flac', 'aac'].includes(format.value)
        );
      case 'ARCHIVE':
        return this.allFileFormats.filter((format) =>
          ['zip', 'rar', '7z', 'tar', 'gz'].includes(format.value)
        );
      default:
        return this.allFileFormats;
    }
  }

  /**
   * Handle file category change to update file format options
   */
  onFileCategoryChange(config: UploadDetailConfig): void {
    // Reset file format when category changes
    config.fileFormat = [];
  }

  /**
   * Get translated file category options
   */
  getTranslatedFileCategoryOptions(): any[] {
    return this.fileCategoryOptions.map((option) => ({
      value: option.value,
      label: this.translateService.instant(option.label),
    }));
  }

  /**
   * Get translated size unit options
   */
  getTranslatedSizeUnitOptions(): any[] {
    return this.sizeUnitOptions.map((option) => ({
      value: option.value,
      label: this.translateService.instant(option.label),
    }));
  }

  /**
   * Get translated file format options based on file category
   */
  getTranslatedFileFormatOptions(fileCategory: string): any[] {
    const formats = this.getFileFormatOptions(fileCategory);
    return formats.map((format) => ({
      value: format.value,
      label: this.translateService.instant(format.label),
    }));
  }

  // Options for form controls
  fileCategoryOptions = FILE_CATEGORIES;
  sizeUnitOptions = SIZE_UNITS;
  allFileFormats = FILE_FORMATS;
  imageFileFormats = IMAGE_FILE_FORMATS;

  constructor(
    private readonly uploadDetailConfigService: UploadDetailConfigService,
    private readonly messageService: MessageService,
    private readonly translateService: TranslateService
  ) {}

  ngOnInit(): void {
    // Migration is now handled automatically in the service constructor
    this.loadConfigurations();
  }

  /**
   * Load configurations from localStorage
   */
  loadConfigurations(): void {
    this.loading.set(true);

    this.uploadDetailConfigService.getAll().subscribe({
      next: (configs) => {
        // Load existing configurations or use defaults
        const detailConfig = configs.find(
          (config) =>
            config.page === 'product-asset' && config.field === 'detail-images'
        );
        const mainConfig = configs.find(
          (config) =>
            config.page === 'product-asset' && config.field === 'main-image'
        );

        if (detailConfig) {
          this.detailImagesConfig = detailConfig;
        }

        if (mainConfig) {
          this.mainImageConfig = mainConfig;
        }

        this.loading.set(false);
      },
      error: (error) => {
        this.loading.set(false);
      },
    });
  }

  /**
   * Select a section in the sidebar
   */
  selectSection(section: string): void {
    this.selectedSection.set(section);
  }

  /**
   * Save configurations to localStorage
   */
  saveConfigurations(): void {
    // Save detail images configuration
    this.uploadDetailConfigService.addOrUpdate({
      id: this.detailImagesConfig.id,
      page: this.detailImagesConfig.page,
      field: this.detailImagesConfig.field,
      fileCategory: this.detailImagesConfig.fileCategory,
      fileFormat: this.detailImagesConfig.fileFormat,
      fileSizeLimit: this.detailImagesConfig.fileSizeLimit,
      sizeUnit: this.detailImagesConfig.sizeUnit,
      maxDetailImages: this.detailImagesConfig.maxDetailImages,
    });

    // Save main image configuration
    this.uploadDetailConfigService.addOrUpdate({
      id: this.mainImageConfig.id,
      page: this.mainImageConfig.page,
      field: this.mainImageConfig.field,
      fileCategory: this.mainImageConfig.fileCategory,
      fileFormat: this.mainImageConfig.fileFormat,
      fileSizeLimit: this.mainImageConfig.fileSizeLimit,
      sizeUnit: this.mainImageConfig.sizeUnit,
      maxDetailImages: this.mainImageConfig.maxDetailImages,
    });

    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('common.actions.success'),
      detail: this.translateService.instant(
        'upload_detail_config.messages.success.configurations_saved'
      ),
    });
  }

  /**
   * Reset configurations to default values
   */
  resetConfigurations(): void {
    // Reset detail images configuration
    this.detailImagesConfig = {
      page: 'product-asset',
      field: 'detail-images',
      fileCategory: 'IMAGE',
      fileFormat: ['jpg', 'jpeg', 'png', 'webp', 'bmp', 'svg'],
      fileSizeLimit: 2,
      sizeUnit: 'MB',
      maxDetailImages: 36,
    };

    // Reset main image configuration
    this.mainImageConfig = {
      page: 'product-asset',
      field: 'main-image',
      fileCategory: 'IMAGE',
      fileFormat: ['jpg', 'jpeg', 'png'],
      fileSizeLimit: 2,
      sizeUnit: 'MB',
      maxDetailImages: 1,
    };

    this.messageService.add({
      severity: 'info',
      summary: this.translateService.instant('common.actions.info'),
      detail: this.translateService.instant(
        'upload_detail_config.messages.success.configurations_reset'
      ),
    });
  }
}
